import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ProjectsService } from '../../../core/services/projects.service';
import { Project, Phase } from '../../../core/models/project.model';
import { LucideIconsModule } from '../../../shared/lucide-icons.module';
import { NotificationService } from '../../../services/notification.service';
import { PropertyUploadService } from '../../../core/services/property-upload.service';
import { PhaseOfferImageRecord, PhaseOfferImagesService } from '../../../core/services/phase-offer-images.service';
import { ConfirmDialogService } from '../../../shared/components/confirm-dialog/confirm-dialog.service';
import { firstValueFrom } from 'rxjs';

@Component({
    selector: 'app-developer-phases',
    standalone: true,
    imports: [CommonModule, FormsModule, LucideIconsModule],
    templateUrl: './developer-phases.component.html',
    styleUrls: ['./developer-phases.component.scss'],
})
export class DeveloperPhasesComponent implements OnInit {
    private projectsService = inject(ProjectsService);
    private notifications = inject(NotificationService);
    private uploadService = inject(PropertyUploadService);
    private offerImagesService = inject(PhaseOfferImagesService);
    private confirm = inject(ConfirmDialogService);

    projects: Project[] = [];
    phases: Phase[] = [];
    selectedProjectId = '';
    loadingProjects = true;
    loadingPhases = false;
    showDialog = false;
    dialogMode: 'create' | 'edit' = 'create';
    dialogName = '';
    dialogProjectId = '';
    dialogDeliveryDate = '';
    dialogStatus: string = 'Active';
    dialogSaving = false;
    editingPhaseId: string | null = null;
    showDeleteDialog = false;
    deletingPhaseId: string | null = null;

    showOfferImagesDialog = false;
    offerImagesPhase: Phase | null = null;
    offerImages: PhaseOfferImageRecord[] = [];
    offerImagesLoading = false;
    offerImagesUploading = false;
    offerImagesSavingOrder = false;
    offerImagesError: string | null = null;

    ngOnInit(): void {
        this.loadProjects();
    }

    loadPhases() {
        if (!this.selectedProjectId) {
            this.phases = [];
            return;
        }
        this.loadingPhases = true;
        this.projectsService.getPhasesByProjectAuth(this.selectedProjectId).subscribe({
            next: (rows) => {
                this.phases = rows;
                this.loadingPhases = false;
            },
            error: () => {
                this.phases = [];
                this.loadingPhases = false;
            }
        });
    }

    openCreate() {
        this.dialogMode = 'create';
        this.dialogName = '';
        this.dialogProjectId = this.selectedProjectId || (this.projects[0]?.id ?? '');
        this.dialogDeliveryDate = '';
        this.dialogStatus = 'Active';
        this.editingPhaseId = null;
        this.showDialog = true;
    }

    openEdit(phase: Phase) {
        this.dialogMode = 'edit';
        this.dialogName = phase.name;
        this.dialogProjectId = phase.project_id || this.selectedProjectId;
        this.dialogDeliveryDate = phase.deliveryDate || '';
        this.dialogStatus = phase.status || 'Active';
        this.editingPhaseId = phase.id;
        this.showDialog = true;
    }

    closeDialog() {
        this.showDialog = false;
        this.dialogSaving = false;
        this.dialogName = '';
        this.dialogDeliveryDate = '';
        this.dialogStatus = 'Active';
        this.editingPhaseId = null;
    }

    saveDialog() {
        if (!this.dialogName.trim()) return;
        if (!this.dialogDeliveryDate) {
            this.notifications.showError('Delivery date is required').subscribe();
            return;
        }
        this.dialogSaving = true;
        if (this.dialogMode === 'create') {
            const projectId = this.dialogProjectId || this.selectedProjectId;
            if (!projectId) {
                this.dialogSaving = false;
                return;
            }
            this.projectsService.createPhase(projectId, {
                name: this.dialogName.trim(),
                deliveryDate: this.dialogDeliveryDate,
                status: this.dialogStatus || 'Active'
            }).subscribe({
                next: () => {
                    this.dialogSaving = false;
                    this.showDialog = false;
                    this.loadPhases();
                },
                error: () => {
                    this.dialogSaving = false;
                    this.notifications.showError('Failed to create phase').subscribe();
                }
            });
        } else if (this.editingPhaseId) {
            this.projectsService.updatePhase(this.editingPhaseId, {
                name: this.dialogName.trim(),
                deliveryDate: this.dialogDeliveryDate,
                status: this.dialogStatus || 'Active'
            }).subscribe({
                next: () => {
                    this.dialogSaving = false;
                    this.showDialog = false;
                    this.loadPhases();
                },
                error: () => {
                    this.dialogSaving = false;
                    this.notifications.showError('Failed to update phase').subscribe();
                }
            });
        }
    }

    openDelete(phase: Phase) {
        this.deletingPhaseId = phase.id;
        this.showDeleteDialog = true;
    }

    closeDeleteDialog() {
        this.showDeleteDialog = false;
        this.deletingPhaseId = null;
        this.dialogSaving = false;
    }

    confirmDelete() {
        if (!this.deletingPhaseId) return;
        this.dialogSaving = true;
        this.projectsService.deletePhase(this.deletingPhaseId).subscribe({
            next: () => {
                this.dialogSaving = false;
                this.closeDeleteDialog();
                this.loadPhases();
            },
            error: () => {
                this.dialogSaving = false;
                this.notifications.showError('Failed to delete phase').subscribe();
            }
        });
    }

    openOfferImages(phase: Phase) {
        this.offerImagesPhase = phase;
        this.showOfferImagesDialog = true;
        this.offerImages = [];
        this.offerImagesError = null;
        this.refreshOfferImages();
    }

    closeOfferImages() {
        this.showOfferImagesDialog = false;
        this.offerImagesPhase = null;
        this.offerImages = [];
        this.offerImagesError = null;
        this.offerImagesLoading = false;
        this.offerImagesUploading = false;
        this.offerImagesSavingOrder = false;
    }

    refreshOfferImages() {
        const phaseId = this.offerImagesPhase?.id ? String(this.offerImagesPhase.id) : '';
        if (!phaseId) return;
        this.offerImagesLoading = true;
        this.offerImagesError = null;
        this.offerImagesService.list(phaseId).subscribe({
            next: (items) => {
                this.offerImages = (items || []).slice().sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
                this.offerImagesLoading = false;
            },
            error: (err) => {
                this.offerImagesLoading = false;
                this.offerImagesError = err?.error?.error?.message || err?.error?.message || err?.message || 'Failed to load offer images';
            }
        });
    }

    async onOfferImagesSelected(event: Event) {
        const phaseId = this.offerImagesPhase?.id ? String(this.offerImagesPhase.id) : '';
        if (!phaseId || this.offerImagesUploading) return;

        const input = event.target as HTMLInputElement;
        const files = Array.from(input.files || []);
        input.value = '';
        if (!files.length) return;

        this.offerImagesUploading = true;
        this.offerImagesError = null;
        try {
            const startingOrder = this.offerImages.length ? Math.max(...this.offerImages.map((i) => i.sortOrder || 0)) + 1 : 0;
            for (let i = 0; i < files.length; i += 1) {
                const file = files[i];
                if (!file.type.startsWith('image/')) continue;

                const uploaded = await firstValueFrom(this.uploadService.uploadFile(file, { isPublic: true, owner: 'org' }));
                const fileId = uploaded?.file_id ? String(uploaded.file_id).trim() : '';
                if (!fileId) throw new Error('Upload failed');

                await firstValueFrom(this.offerImagesService.add(phaseId, { fileId, sortOrder: startingOrder + i }));
            }
            this.notifications.showSuccess('Offer images updated').subscribe();
            this.refreshOfferImages();
        } catch (err: any) {
            this.offerImagesError = err?.error?.error?.message || err?.error?.message || err?.message || 'Failed to upload offer images';
            this.notifications.showError(this.offerImagesError).subscribe();
        } finally {
            this.offerImagesUploading = false;
        }
    }

    async deleteOfferImage(img: PhaseOfferImageRecord) {
        const phaseId = this.offerImagesPhase?.id ? String(this.offerImagesPhase.id) : '';
        if (!phaseId || !img?.id || this.offerImagesUploading || this.offerImagesSavingOrder) return;

        const ok = await this.confirm.confirm({
            title: 'Remove offer image?',
            message: 'This will remove the image from this phase. It will not delete the file from storage.',
            confirmText: 'Remove',
            cancelText: 'Cancel',
            tone: 'danger'
        });
        if (!ok) return;

        this.offerImagesService.remove(phaseId, img.id).subscribe({
            next: () => {
                this.notifications.showSuccess('Offer image removed').subscribe();
                this.refreshOfferImages();
            },
            error: (err) => {
                const message = err?.error?.error?.message || err?.error?.message || err?.message || 'Failed to remove offer image';
                this.notifications.showError(message).subscribe();
            }
        });
    }

    moveOfferImage(index: number, delta: number) {
        if (this.offerImagesSavingOrder) return;
        const phaseId = this.offerImagesPhase?.id ? String(this.offerImagesPhase.id) : '';
        if (!phaseId) return;
        const nextIndex = index + delta;
        if (nextIndex < 0 || nextIndex >= this.offerImages.length) return;

        const copy = this.offerImages.slice();
        const [item] = copy.splice(index, 1);
        copy.splice(nextIndex, 0, item);
        this.offerImages = copy;

        this.offerImagesSavingOrder = true;
        const ids = copy.map((r) => r.id);
        this.offerImagesService.reorder(phaseId, ids).subscribe({
            next: () => {
                this.offerImagesSavingOrder = false;
                this.refreshOfferImages();
            },
            error: (err) => {
                this.offerImagesSavingOrder = false;
                const message = err?.error?.error?.message || err?.error?.message || err?.message || 'Failed to reorder images';
                this.offerImagesError = message;
                this.notifications.showError(message).subscribe();
                this.refreshOfferImages();
            }
        });
    }

    private loadProjects() {
        this.loadingProjects = true;
        this.projectsService.getProjectsAuth().subscribe({
            next: ({ items }) => {
                this.projects = items;
                this.loadingProjects = false;
                if (items.length) {
                    this.selectedProjectId = items[0].id || '';
                    this.loadPhases();
                }
            },
            error: () => {
                this.projects = [];
                this.loadingProjects = false;
            }
        });
    }
}
